'use client';

import { Link, createFileRoute } from '@tanstack/react-router';
import {
  CalendarRange,
  Calculator,
  Settings,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { PageHeader } from '@/components/page-header';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useDeleteBoundary } from '@/features/ghg/api/use-boundary';
import { GHGOnboardingWizard } from '@/features/ghg/components/GHGOnboardingWizard';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/ghg/setup/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.ghgSetup'),
  }),
  component: GhgSetupPage,
});

const OPTIONAL_CONFIG_CARDS = [
  {
    to: '/settings/reporting-periods',
    icon: CalendarRange,
    titleKey: 'ghgSetupPage.reportingPeriodsTitle',
    descriptionKey: 'ghgSetupPage.reportingPeriodsDescription',
  },
  {
    to: '/ghg/emission-factors',
    icon: Calculator,
    titleKey: 'ghgSetupPage.emissionFactorsTitle',
    descriptionKey: 'ghgSetupPage.emissionFactorsDescription',
  },
] as const;

function GhgSetupPage() {
  const { t } = useTranslation('ghg');
  const { isAdmin } = useOrgRole();
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const deleteBoundary = useDeleteBoundary();

  return (
    <div className="p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <PageHeader
          title={t('ghgSetupPage.title')}
          description={t('ghgSetupPage.description')}
          module="ghg"
          icon={<Settings className="h-6 w-6" />}
        />

        <GHGOnboardingWizard data-testid="ghg-setup-wizard" />

        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                setResetDialogOpen(true);
              }}
              disabled={deleteBoundary.isPending}
            >
              {deleteBoundary.isPending ? t('ghgSetupPage.resetting') : t('ghgSetupPage.resetSetup')}
            </Button>
          </div>
        )}

        {isAdmin && (
          <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('ghgSetupPage.resetDialogTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('ghgSetupPage.resetDialogDescription')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('ghgSetupPage.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    if (deleteBoundary.isPending) return;
                    setResetDialogOpen(false);
                    deleteBoundary.mutate(undefined, {
                      onSuccess: () => {
                        toast.success(t('toast.ghgSetupResetStartAgainFromStep1'));
                      },
                      onError: (error) => {
                        toast.error(t('toast.failedToResetGHGSetup'), {
                          description: error instanceof Error ? error.message : t('ghgSetupPage.unknownError'),
                        });
                      },
                    });
                  }}
                  disabled={deleteBoundary.isPending}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleteBoundary.isPending ? t('ghgSetupPage.resetting') : t('ghgSetupPage.resetSetup')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        <div
          className="grid gap-4 sm:grid-cols-2"
          data-testid="optional-config-grid"
        >
          {OPTIONAL_CONFIG_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <Link key={card.to} to={card.to} className="block">
                <Card className="h-full transition-colors hover:bg-muted/50">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                        <Icon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{t(card.titleKey)}</CardTitle>
                        <CardDescription className="text-xs">
                          {t(card.descriptionKey)}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
